import { useState, useEffect } from "react";
import {
  Eye,
  EyeOff,
  AlignStartHorizontal,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Italic,
  Underline,
  CaseUpper,
  CaseLower,
  Loader2,
  MoveHorizontal,
  MoveVertical,
  Minus,
  X,
  Shrink,
} from "lucide-react";
import { useBuilderNode } from '../../hooks/useBuilderNode';
import { useFabricCanvas } from '../../hooks/useFabricCanvas';
import { ElementColorPicker } from "../ElementColorPicker";
import { GOOGLE_FONTS, WEIGHT_LABELS, normalizeWeight } from '../../lib/fonts';
import { buildCurvePath } from '../../utils/curveUtils';
import type { CurvePreset } from '../../utils/curveUtils';
import type { FabricObject } from "fabric";
import type { ShapeNodeData, TextNodeData, SvgNodeData } from '../../types/node.types';

// ── Reusable pieces ───────────────────────────────────────────────

const Section = ({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <div className="border-t border-gray-100">
    <div className="flex items-center justify-between px-3 py-2">
      <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">{title}</span>
      {actions && <div className="flex items-center gap-0.5">{actions}</div>}
    </div>
    {children && <div className="px-3 pb-2 space-y-1.5">{children}</div>}
  </div>
);

const IconBtn = ({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    title={title}
    className="p-1 rounded transition-colors text-gray-400 hover:text-gray-700 hover:bg-gray-100"
  >
    {children}
  </button>
);

interface InputItem {
  id: string;
  label: React.ReactNode;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
}

const InputPair = ({ a, b }: { a: InputItem; b: InputItem }) => (
  <div className="grid grid-cols-2 gap-1.5">
    {[a, b].map(({ id, label, value, onChange, suffix, min, max, step }) => (
      <div
        key={id}
        className="flex items-center gap-1 bg-gray-100 rounded-md px-2 py-1 focus-within:ring-1 focus-within:ring-blue-400"
      >
        <span className="text-[10px] text-gray-400 flex-shrink-0 w-3 text-center select-none">
          {label}
        </span>
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 bg-transparent text-xs text-gray-800 focus:outline-none min-w-0 w-full"
        />
        {suffix && (
          <span className="text-[10px] text-gray-400 flex-shrink-0">{suffix}</span>
        )}
      </div>
    ))}
  </div>
);


// ── Toggle button for style options ─────────────────────────────

const StyleToggle = ({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    title={title}
    className={`flex-1 flex items-center justify-center rounded transition-colors ${
      active
        ? "bg-gray-800 text-white"
        : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

// ── Curve/warp preset icons ─────────────────────────────────────

const ArchUpIcon = () => (
  <svg width={12} height={12} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
    <path d="M 1 11 Q 7 1 13 11" />
  </svg>
);

const ArchDownIcon = () => (
  <svg width={12} height={12} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
    <path d="M 1 3 Q 7 13 13 3" />
  </svg>
);

const CircleTextIcon = () => (
  <svg width={12} height={12} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
    <circle cx={7} cy={7} r={5.5} />
  </svg>
);

// ── Alignment section ─────────────────────────────────────────────

const AlignBtn = ({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    title={title}
    className="flex-1 flex items-center justify-center py-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
  >
    {children}
  </button>
);

const AlignSection = ({
  selectedNode,
  updateNodePosition,
  canvasRef,
}: {
  selectedNode: NonNullable<ReturnType<typeof useBuilderNode>["selectedNode"]>;
  updateNodePosition: ReturnType<typeof useBuilderNode>["updateNodePosition"];
  canvasRef: ReturnType<typeof useFabricCanvas>["canvasRef"];
}) => {
  const align = (type: "left" | "hcenter" | "right" | "top" | "vcenter" | "bottom") => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const obj = canvas.getObjects().find((o) => (o as FabricObject & { id?: string }).id === selectedNode.id);
    if (!obj) return;

    const cw = canvas.width ?? 0;
    const ch = canvas.height ?? 0;
    const ow = obj.getScaledWidth();
    const oh = obj.getScaledHeight();

    let { x, y } = selectedNode.position;

    switch (type) {
      case "left":    x = 0;              break;
      case "hcenter": x = (cw - ow) / 2; break;
      case "right":   x = cw - ow;       break;
      case "top":     y = 0;              break;
      case "vcenter": y = (ch - oh) / 2; break;
      case "bottom":  y = ch - oh;       break;
    }

    updateNodePosition(selectedNode.id, { x, y });
  };

  return (
    <Section title="Align to canvas">
      <div className="flex gap-0.5 -mx-1">
        <AlignBtn onClick={() => align("left")} title="Align left">
          <AlignStartVertical size={13} />
        </AlignBtn>
        <AlignBtn onClick={() => align("hcenter")} title="Align center">
          <AlignCenterVertical size={13} />
        </AlignBtn>
        <AlignBtn onClick={() => align("right")} title="Align right">
          <AlignEndVertical size={13} />
        </AlignBtn>
        <AlignBtn onClick={() => align("top")} title="Align top">
          <AlignStartHorizontal size={13} />
        </AlignBtn>
        <AlignBtn onClick={() => align("vcenter")} title="Align middle">
          <AlignCenterHorizontal size={13} />
        </AlignBtn>
        <AlignBtn onClick={() => align("bottom")} title="Align bottom">
          <AlignEndHorizontal size={13} />
        </AlignBtn>
      </div>
    </Section>
  );
};

// ── Helper to read fill from any node type ────────────────────────

const getNodeFill = (node: NonNullable<ReturnType<typeof useBuilderNode>["selectedNode"]>): string | null => {
  switch (node.type) {
    case "shape": return (node.data as ShapeNodeData).fill;
    case "text":  return (node.data as TextNodeData).fill;
    case "svg":   return (node.data as SvgNodeData).fill;
    default:      return null;
  }
};

// ── Curve preset detection ────────────────────────────────────────

const detectCurvePreset = (data: TextNodeData): CurvePreset => {
  // Prefer the stored preset name (new data model)
  if (data.curvePreset && data.curvePreset !== 'none') return data.curvePreset as CurvePreset;
  // Fallback for legacy data without curvePreset field
  if (!data.curvePathData) return "none";
  if (data.curveAmount && data.curveAmount > 0) return "arch-up";
  if (data.curveAmount && data.curveAmount < 0) return "arch-down";
  if (data.curvePathData.includes("1,1")) return "circle";
  return "arch-up";
};

// ── Main component ────────────────────────────────────────────────

const NodeProperties = () => {
  const {
    selectedNode,
    updateNodePosition,
    updateNodeSize,
    updateNodeRotation,
    updateNodeOpacity,
    toggleNodeVisibility,
    updateNodeFill,
    updateNodeStroke,
    updateNodeTextProp,
    updateNodeCurve,
    fitTextToContent,
  } = useBuilderNode();

  const { canvasRef } = useFabricCanvas();

  // Local curve state for responsive slider
  const textData = selectedNode?.type === "text" ? (selectedNode.data as TextNodeData) : null;
  const [curvePreset, setCurvePreset] = useState<CurvePreset>(() =>
    textData ? detectCurvePreset(textData) : "none"
  );
  const [curveAmount, setCurveAmount] = useState(() => textData?.curveAmount ?? 50);
  const [curveRadius, setCurveRadius] = useState(() =>
    textData?.curveRadius ?? Math.round((selectedNode?.size.width ?? 200) / 2)
  );
  const [pathOffset, setPathOffset] = useState(() => textData?.pathStartOffset ?? 0);
  const [fontLoading, setFontLoading] = useState(false);

  // Sync local curve state when selected node changes or data reloads
  useEffect(() => {
    if (!textData) return;
    setCurvePreset(detectCurvePreset(textData));
    setCurveAmount(textData.curveAmount ?? 50);
    setCurveRadius(textData.curveRadius ?? Math.round((selectedNode?.size.width ?? 200) / 2));
    setPathOffset(textData.pathStartOffset ?? 0);
  }, [selectedNode?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!selectedNode) {
    return (
      <div className="w-full bg-white rounded-lg shadow-xs border border-gray-200 px-3 py-6">
        <p className="text-xs text-gray-400 text-center">No element selected</p>
      </div>
    );
  }

  const handleFontChange = async (family: string) => {
    setFontLoading(true);
    await updateNodeTextProp(selectedNode.id, "fontFamily", family);
    setFontLoading(false);
  };

  const handleWeightChange = async (weight: number) => {
    setFontLoading(true);
    await updateNodeTextProp(selectedNode.id, "fontWeight", weight);
    setFontLoading(false);
  };

  const currentFont = GOOGLE_FONTS.find((f) => f.family === textData?.fontFamily);
  const availableWeights = currentFont?.weights ?? [400, 700];
  const currentWeight = normalizeWeight(textData?.fontWeight);

  const handleCurvePresetChange = (preset: CurvePreset) => {
    setCurvePreset(preset);
    if (preset === "none") setPathOffset(0);
    const width = selectedNode.size.width;
    const radius = preset === "circle" ? curveRadius : undefined;
    const pathData = buildCurvePath(preset, width, curveAmount, radius);
    updateNodeCurve(selectedNode.id, pathData, preset === "none" ? 0 : curveAmount, radius, preset, preset === "none" ? 0 : undefined);
  };

  const handleCurveAmountChange = (amount: number) => {
    setCurveAmount(amount);
    const width = selectedNode.size.width;
    const pathData = buildCurvePath(curvePreset, width, amount);
    updateNodeCurve(selectedNode.id, pathData, amount, undefined, curvePreset, undefined);
  };

  const handleCurveRadiusChange = (raw: number) => {
    const radius = Math.round(raw * 2) / 2; // snap to 0.5 increments
    setCurveRadius(radius);
    const width = selectedNode.size.width;
    const pathData = buildCurvePath("circle", width, curveAmount, radius);
    updateNodeCurve(selectedNode.id, pathData, curveAmount, radius, "circle", undefined);
  };

  const handlePathOffsetChange = (offset: number) => {
    setPathOffset(offset);
    const width = selectedNode.size.width;
    const radius = curvePreset === "circle" ? curveRadius : undefined;
    const pathData = buildCurvePath(curvePreset, width, curveAmount, radius);
    updateNodeCurve(selectedNode.id, pathData, curveAmount, radius, curvePreset, offset);
  };

  const fill = getNodeFill(selectedNode);

  return (
    <div className="w-full bg-white rounded-lg shadow-xs border border-gray-200 overflow-y-auto">
      {/* ── Align ────────────────────────────────────────────────── */}
      <AlignSection
        selectedNode={selectedNode}
        updateNodePosition={updateNodePosition}
        canvasRef={canvasRef}
      />

      {/* ── Position ─────────────────────────────────────────────── */}
      <Section title="Position">
        <InputPair
          a={{
            id: "pos-x",
            label: "X",
            value: Math.round(selectedNode.position.x),
            onChange: (v) =>
              updateNodePosition(selectedNode.id, {
                ...selectedNode.position,
                x: v,
              }),
          }}
          b={{
            id: "pos-y",
            label: "Y",
            value: Math.round(selectedNode.position.y),
            onChange: (v) =>
              updateNodePosition(selectedNode.id, {
                ...selectedNode.position,
                y: v,
              }),
          }}
        />
      </Section>

      {/* ── Dimensions ───────────────────────────────────────────── */}
      <Section title="Dimensions">
        <div className="flex items-center gap-1.5">
          <div className="grid grid-cols-2 gap-1.5 flex-1">
            {[
              {
                id: "dim-w",
                label: "W",
                value: Math.round(selectedNode.size.width),
                onChange: (v: number) =>
                  updateNodeSize(selectedNode.id, { ...selectedNode.size, width: v }),
              },
              {
                id: "dim-h",
                label: "H",
                value: Math.round(selectedNode.size.height),
                onChange: (v: number) =>
                  updateNodeSize(selectedNode.id, { ...selectedNode.size, height: v }),
              },
            ].map(({ id, label, value, onChange }) => (
              <div
                key={id}
                className="flex items-center gap-1 bg-gray-100 rounded-md px-2 py-1 focus-within:ring-1 focus-within:ring-blue-400"
              >
                <span className="text-[10px] text-gray-400 flex-shrink-0 w-3 text-center select-none">
                  {label}
                </span>
                <input
                  type="number"
                  value={value}
                  onChange={(e) => onChange(Number(e.target.value))}
                  className="flex-1 bg-transparent text-xs text-gray-800 focus:outline-none min-w-0 w-full"
                />
              </div>
            ))}
          </div>
          {selectedNode.type === "text" && (
            <button
              onClick={() => fitTextToContent(selectedNode.id)}
              title="Fit width to text content"
              className="hover:text-gray-700 hover:bg-gray-100 p-1 rounded transition-colors text-gray-400"
            >
              <Shrink size={12} />
            </button>
          )}
        </div>
      </Section>

      {/* ── Appearance ───────────────────────────────────────────── */}
      <Section
        title="Appearance"
        actions={
          <IconBtn
            onClick={() => toggleNodeVisibility(selectedNode.id)}
            title={selectedNode.visible ? "Hide" : "Show"}
          >
            {selectedNode.visible ? (
              <Eye size={13} />
            ) : (
              <EyeOff size={13} className="text-gray-300" />
            )}
          </IconBtn>
        }
      >
        <InputPair
          a={{
            id: "rotation",
            label: "↻",
            value: Math.round(selectedNode.rotation),
            min: 0,
            max: 360,
            onChange: (v) => updateNodeRotation(selectedNode.id, v),
            suffix: "°",
          }}
          b={{
            id: "opacity",
            label: "◻",
            value: Math.round(selectedNode.opacity * 100),
            min: 0,
            max: 100,
            onChange: (v) => updateNodeOpacity(selectedNode.id, v / 100),
            suffix: "%",
          }}
        />
      </Section>

      {/* ── Fill ─────────────────────────────────────────────────── */}
      {fill !== null && (
        <Section title="Fill">
          {selectedNode.type === "shape" ? (
            <div className="grid grid-cols-2 gap-1.5">
              <ElementColorPicker
                color={fill}
                onChange={(c) => updateNodeFill(selectedNode.id, c)}
                label="F"
              />
              <ElementColorPicker
                color={(selectedNode.data as ShapeNodeData).stroke || "#000000"}
                onChange={(c) => updateNodeStroke(selectedNode.id, c)}
                label="S"
              />
            </div>
          ) : (
            <ElementColorPicker
              color={fill}
              onChange={(c) => updateNodeFill(selectedNode.id, c)}
            />
          )}
        </Section>
      )}

      {/* ── Text ─────────────────────────────────────────────────── */}
      {selectedNode.type === "text" && textData && (
        <Section title="Text">
          {/* Row 1: Alignment */}
          <div className="flex bg-gray-100 rounded-md overflow-hidden max-w-[50%] h-[25px]">
            {(["left", "center", "right"] as const).map((align) => (
              <StyleToggle
                key={align}
                active={textData.textAlign === align}
                onClick={() => updateNodeTextProp(selectedNode.id, "textAlign", align)}
                title={`Align ${align}`}
              >
                {align === "left" && <AlignLeft size={12} />}
                {align === "center" && <AlignCenter size={12} />}
                {align === "right" && <AlignRight size={12} />}
              </StyleToggle>
            ))}
          </div>

          {/* Row 2: Font family + size */}
          <div className="grid grid-cols-[2fr_1fr] gap-1.5">
            <div className="flex items-center gap-1 bg-gray-100 rounded-md px-2 py-1 focus-within:ring-1 focus-within:ring-blue-400">
              {fontLoading && (
                <Loader2 size={10} className="text-gray-400 flex-shrink-0 animate-spin" />
              )}
              <select
                value={textData.fontFamily}
                onChange={(e) => handleFontChange(e.target.value)}
                className="flex-1 bg-transparent text-xs text-gray-800 focus:outline-none min-w-0 w-full"
              >
                {GOOGLE_FONTS.map((f) => (
                  <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>
                    {f.family}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1 bg-gray-100 rounded-md px-2 py-1 focus-within:ring-1 focus-within:ring-blue-400">
              <input
                type="number"
                min={1}
                max={400}
                value={textData.fontSize}
                onChange={(e) => updateNodeTextProp(selectedNode.id, "fontSize", Number(e.target.value))}
                className="flex-1 bg-transparent text-xs text-gray-800 focus:outline-none min-w-0 w-full"
              />
              <span className="text-[10px] text-gray-400 flex-shrink-0">px</span>
            </div>
          </div>

          {/* Row 3: Font weight + appearance toggles */}
          <div className="grid grid-cols-[1fr_1fr] gap-1.5">
            <div className="flex items-center gap-1 bg-gray-100 rounded-md px-2 py-1 focus-within:ring-1 focus-within:ring-blue-400">
              <select
                value={currentWeight}
                onChange={(e) => handleWeightChange(Number(e.target.value))}
                className="flex-1 bg-transparent text-xs text-gray-800 focus:outline-none min-w-0 w-full"
              >
                {availableWeights.map((w) => (
                  <option key={w} value={w}>
                    {WEIGHT_LABELS[w] ?? w}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex bg-gray-100 rounded-md overflow-hidden h-[26px]">
              <StyleToggle
                active={textData.fontStyle === "italic"}
                onClick={() => updateNodeTextProp(selectedNode.id, "fontStyle", textData.fontStyle === "italic" ? "normal" : "italic")}
                title="Italic"
              ><Italic size={12} /></StyleToggle>
              <StyleToggle
                active={textData.underline === true}
                onClick={() => updateNodeTextProp(selectedNode.id, "underline", !textData.underline)}
                title="Underline"
              ><Underline size={12} /></StyleToggle>
              <StyleToggle
                active={textData.uppercase === true}
                onClick={() => updateNodeTextProp(selectedNode.id, "uppercase", !textData.uppercase)}
                title="Uppercase"
              ><CaseUpper size={12} /></StyleToggle>
              <StyleToggle
                active={textData.lowercase === true}
                onClick={() => updateNodeTextProp(selectedNode.id, "lowercase", !textData.lowercase)}
                title="Lowercase"
              ><CaseLower size={12} /></StyleToggle>
            </div>
          </div>

          {/* Row 4: Line height + letter spacing */}
          <div className="grid grid-cols-2 gap-1.5">
            <div className="flex items-center gap-1 bg-gray-100 rounded-md px-2 py-1 focus-within:ring-1 focus-within:ring-blue-400">
              <span className="text-[10px] text-gray-400 flex-shrink-0 w-3 text-center select-none">
                <MoveVertical size={11} />
              </span>
              <input
                type="number"
                min={0.5}
                max={3}
                step={0.1}
                value={textData.lineHeight ?? 1.16}
                onChange={(e) => updateNodeTextProp(selectedNode.id, "lineHeight", Number(e.target.value))}
                className="flex-1 bg-transparent text-xs text-gray-800 focus:outline-none min-w-0 w-full [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
              />
              <span className="text-[10px] text-gray-400 flex-shrink-0">px</span>
            </div>
            <div className="flex items-center gap-1 bg-gray-100 rounded-md px-2 py-1 focus-within:ring-1 focus-within:ring-blue-400">
              <span className="text-[10px] text-gray-400 flex-shrink-0 w-3 text-center select-none">
                <MoveHorizontal size={11} />
              </span>
              <input
                type="number"
                min={-200}
                max={800}
                step={10}
                value={textData.charSpacing ?? 0}
                onChange={(e) => updateNodeTextProp(selectedNode.id, "charSpacing", Number(e.target.value))}
                className="flex-1 bg-transparent text-xs text-gray-800 focus:outline-none min-w-0 w-full [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
              />
              <span className="text-[10px] text-gray-400 flex-shrink-0">px</span>
            </div>
          </div>
        </Section>
      )}

      {/* ── Effects (text warp) ─────────────────────────────────── */}
      {selectedNode.type === "text" && textData && (
        <Section title="Effects">
          {/* Curve preset toggles */}
          <div className="flex bg-gray-100 rounded-md overflow-hidden h-[26px]">
            <StyleToggle
              active={curvePreset === "none"}
              onClick={() => handleCurvePresetChange("none")}
              title="No effect"
            >
              <Minus size={12} />
            </StyleToggle>
            <StyleToggle
              active={curvePreset === "arch-up"}
              onClick={() => handleCurvePresetChange("arch-up")}
              title="Arch up"
            >
              <ArchUpIcon />
            </StyleToggle>
            <StyleToggle
              active={curvePreset === "arch-down"}
              onClick={() => handleCurvePresetChange("arch-down")}
              title="Arch down"
            >
              <ArchDownIcon />
            </StyleToggle>
            <StyleToggle
              active={curvePreset === "circle"}
              onClick={() => handleCurvePresetChange("circle")}
              title="Circle"
            >
              <CircleTextIcon />
            </StyleToggle>
          </div>

          {/* Effect sliders with title + remove */}
          {curvePreset !== "none" && (
            <div className="space-y-2.5 mt-1">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-gray-600 select-none">
                  {curvePreset === "arch-up" ? "Arch Up" : curvePreset === "arch-down" ? "Arch Down" : "Circle"}
                </span>
                <button
                  onClick={() => handleCurvePresetChange("none")}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Remove effect"
                >
                  <X size={12} />
                </button>
              </div>

              {/* Bend slider (arch-up / arch-down) */}
              {(curvePreset === "arch-up" || curvePreset === "arch-down") && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-400 w-9 flex-shrink-0 select-none">Bend</span>
                  <input
                    type="range"
                    min={1}
                    max={100}
                    value={curveAmount}
                    onChange={(e) => handleCurveAmountChange(Number(e.target.value))}
                    className="slider-sm flex-1"
                  />
                  <span className="text-[11px] text-gray-400 w-6 text-right tabular-nums whitespace-nowrap">{curveAmount}</span>
                </div>
              )}

              {/* Radius slider (circle mode) */}
              {curvePreset === "circle" && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-400 w-9 flex-shrink-0 select-none">Size</span>
                  <input
                    type="range"
                    min={20}
                    max={500}
                    step={0.5}
                    value={curveRadius}
                    onChange={(e) => handleCurveRadiusChange(Number(e.target.value))}
                    className="slider-sm flex-1"
                  />
                  <span className="text-[11px] text-gray-400 w-6 text-right tabular-nums whitespace-nowrap">{curveRadius}</span>
                </div>
              )}

              {/* Offset slider */}
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-400 w-9 flex-shrink-0 select-none">Offset</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={pathOffset}
                  onChange={(e) => handlePathOffsetChange(Number(e.target.value))}
                  className="slider-sm flex-1"
                />
                <span className="text-[11px] text-gray-400 w-6 text-right tabular-nums">{pathOffset}</span>
              </div>
            </div>
          )}
        </Section>
      )}
    </div>
  );
};

export { NodeProperties };
